import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { todayStr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import MobileLeave from "./MobileLeave";
import { Button } from "@/components/ui/button";
import { Crosshair, Phone, SignOut } from "@phosphor-icons/react";

export default function MobileProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [guard, setGuard] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [firearm, setFirearm] = useState(null);

  useEffect(() => {
    if (user?.guard_id) {
      api.get("/guards").then((r) => {
        setGuard(r.data.find((g) => g.id === user.guard_id) || null);
      }).catch(() => {});
    }
    // ⚠️ Resolved by the SERVER. /users is 403 for a guard and must stay that
    // way, which is exactly why the field app had no way to name a supervisor
    // at all -- a guard on a post at 02:00 had nobody to ring.
    api.get("/my/contacts").then((r) => setContacts(r.data)).catch(() => {});
    // A guard put their name to a serial number and a round count. They are
    // entitled to see exactly what that was without asking the office, and to
    // check it against what is in their holster before they hand it back.
    if (user?.guard_id) {
      api.get(`/guards/${user.guard_id}/firearms`).then((r) => setFirearm(r.data)).catch(() => {});
    }
  }, [user]);

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  };

  const today = todayStr();

  return (
    <div className="p-4" data-testid="mobile-profile">
      <div className="border border-border bg-card p-4 mb-4">
        <div className="font-heading font-black text-xl">{user.name}</div>
        <div className="font-mono text-[10px] uppercase text-muted-foreground">{user.email}</div>
        {guard && (
          <div className="mt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Employee no.</span><span className="font-mono text-xs">{guard.employee_no}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Branch</span><span className="text-xs">{guard.branch_name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Status</span><StatusBadge value={guard.employment_status} /></div>
          </div>
        )}
      </div>

      {firearm?.current && (
        <div className="mb-6" data-testid="mobile-firearm">
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">
            What you are signed for
          </div>
          <div className="border border-[hsl(var(--warning))] bg-card p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-heading font-black text-lg leading-tight">
                  {firearm.current.make} {firearm.current.model}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {firearm.current.firearm_type} · {firearm.current.caliber}
                </div>
              </div>
              <Crosshair size={22} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Serial no.</div>
                <div className="font-mono text-sm font-bold">{firearm.current.serial_no}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Rounds</div>
                <div className="font-mono text-sm font-bold">{firearm.current.rounds_out}</div>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground leading-relaxed mt-3">
              Check the serial against the unit in your holster before you hand it back. Both the
              firearm and all {firearm.current.rounds_out} round(s) are receipted at the armoury at
              the end of your detail, and any round that does not come back has to be explained.
            </div>
          </div>
        </div>
      )}

      {contacts && (
        <div className="mb-6" data-testid="mobile-contacts">
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">Who to call</div>
          <div className="space-y-2">
            {contacts.duty_office && (
              <ContactRow
                label="Duty office" name={contacts.duty_office.name}
                phone={contacts.duty_office.phone} note={contacts.duty_office.address} testid="contact-duty-office" />
            )}
            {(contacts.supervisors || []).map((s, i) => (
              <ContactRow
                key={i} label={s.role === "field_supervisor" ? "Supervisor" : "Operations"}
                name={s.name} phone={s.phone} testid={`contact-supervisor-${i}`} />
            ))}
            {contacts.next_of_kin && (
              <ContactRow
                label={`Your next of kin · ${contacts.next_of_kin.relation || "contact"}`}
                name={contacts.next_of_kin.name} phone={contacts.next_of_kin.phone}
                testid="contact-next-of-kin" />
            )}
            {!contacts.duty_office && !(contacts.supervisors || []).length && (
              <div className="border border-border border-dashed p-4 text-center text-xs text-muted-foreground">
                No contacts on file for your branch yet. Ask the office to add them.
              </div>
            )}
          </div>
        </div>
      )}

      {guard && <MobileLeave />}

      {guard && (
        <>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">My documents</div>
          <div className="space-y-2 mb-6" data-testid="profile-docs">
            {(guard.documents || []).map((d) => {
              const expired = d.expiry && d.expiry < today;
              return (
                <div key={d.id} className={`border p-3 flex items-center justify-between ${expired && d.required ? "border-[hsl(var(--alert))]" : "border-border"}`}>
                  <div>
                    <div className="text-sm font-semibold">{d.type}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{d.number || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className={`font-mono text-xs ${expired ? "text-[hsl(var(--alert))] font-bold" : ""}`}>{d.expiry || "no expiry"}</div>
                    {d.required && <div className="font-mono text-[9px] text-muted-foreground">MANDATORY</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Button variant="outline" className="w-full rounded-none h-11" data-testid="mobile-logout" onClick={doLogout}>
        <SignOut size={16} className="mr-2" /> Sign out
      </Button>
    </div>
  );
}

/**
 * One person a guard can ring.
 *
 * ⚠️ A missing number says so in words. A dash here would read as a broken
 * screen — and worse, a guard would keep tapping it in the one situation where
 * finding out it does nothing costs the most.
 */
function ContactRow({ label, name, phone, note, testid }) {
  const body = (
    <>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-semibold truncate">{name}</div>
        {note && <div className="text-[10px] text-muted-foreground truncate">{note}</div>}
      </div>
      {phone
        ? <div className="flex items-center gap-1.5 shrink-0 text-[hsl(var(--warning))]">
            <Phone size={16} weight="fill" />
            <span className="font-mono text-xs">{phone}</span>
          </div>
        : <span className="font-mono text-[10px] text-muted-foreground shrink-0">no number on file</span>}
    </>
  );
  return phone ? (
    <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} data-testid={testid}
       className="border border-border bg-card p-3 flex items-center justify-between gap-3">
      {body}
    </a>
  ) : (
    <div data-testid={testid} className="border border-border bg-card p-3 flex items-center justify-between gap-3 opacity-70">
      {body}
    </div>
  );
}
